import React, { useState, useRef, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { McTopBar } from '@maersk-global/mds-react-wrapper/components-core/mc-top-bar';
import { McButton } from '@maersk-global/mds-react-wrapper/components-core/mc-button';
import { McAvatar } from '@maersk-global/mds-react-wrapper/components-core/mc-avatar';
import { msalInstance } from '../../auth/msalConfig';
import { APP_CONFIG } from '../../constants/config';
import { COLORS, SPACING, SHADOWS, BORDER_RADIUS } from '../../constants/theme';

/**
 * UserMenu – avatar + dropdown with account info and sign-out.
 */
const UserMenu = () => {
  const { accounts, inProgress } = useMsal();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const account = accounts[0];

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!account) return null;

  const name     = account.name || account.username || 'User';
  const email    = account.username || '';
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map(n => n[0])
    .join('')
    .toUpperCase();

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <McButton
        appearance="neutral"
        variant="plain"
        fit="small"
        title={name}
        active={open}
        ariaexpanded={open ? 'true' : 'false'}
        trailingicon="chevron-down"
        click={() => setOpen(o => !o)}
      >
        <McAvatar slot="icon" initials={initials} fit="x-small" hiddentooltip />
        <span style={{ display: 'inline-block', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
          {name}
        </span>
      </McButton>

      {open && (
        <div style={{
          position: 'absolute',
          top: `calc(100% + ${SPACING.sm}px)`,
          right: 0,
          minWidth: 240,
          backgroundColor: COLORS.white,
          border: `1px solid ${COLORS.border}`,
          borderRadius: BORDER_RADIUS.lg,
          boxShadow: SHADOWS.lg,
          zIndex: 2000,
          overflow: 'hidden',
        }}>
          {/* User info */}
          <div style={{ padding: `${SPACING.lg}px ${SPACING.xl}px`, borderBottom: `1px solid ${COLORS.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.md }}>
              <McAvatar initials={initials} fit="medium" hiddentooltip />
              <div style={{ minWidth: 0 }}>
                <div className="mds-text--medium-bold" style={{
                  color: COLORS.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {name}
                </div>
                {email && (
                  <div className="mds-text--x-small-normal" style={{
                    color: COLORS.textSecondary,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {email}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sign out */}
          <div style={{ padding: SPACING.xs }}>
            <McButton
              label="Sign out"
              icon="door-arrow-right"
              appearance="error"
              variant="plain"
              fit="small"
              width="full-width"
              justifyitems="left"
              click={() => {
                if (inProgress === InteractionStatus.None) {
                  msalInstance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
                }
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Application Header Component
 * MDS top bar: Maersk logo, application title, and signed-in user menu.
 * When `onClick` is given, the logo + title link runs it (SPA navigation) instead of reloading.
 */
const Header = ({ onClick }) => {
  const handleClick = (e) => {
    if (!onClick) return;
    const path = e.nativeEvent.composedPath ? e.nativeEvent.composedPath() : [];
    const onLogo = path.some(el => el.classList && el.classList.contains('logo-and-product'));
    if (onLogo) {
      e.preventDefault();
      onClick(e);
    }
  };

  return (
    <div onClick={handleClick} style={{ padding: `0 ${SPACING.xxl}px`, borderBottom: `1px solid ${COLORS.borderStrong}` }}>
      <McTopBar
        product={APP_CONFIG.name}
        productshort={APP_CONFIG.name}
        href={onClick ? '/' : undefined}
      >
        <div slot="actions" style={{ display: 'flex', alignItems: 'center', gap: SPACING.xl }}>
          <UserMenu />
        </div>
      </McTopBar>
    </div>
  );
};

export default Header;
